import type { ReactElement } from "react";
import type { Angebot, Ansprechpartner, Auftrag, Benutzer, Unternehmen } from "@kundeninfo/api-client";
import { Feld } from "@kundeninfo/ui";
import { Chip, Eingabe } from "@kundeninfo/ui/me-design";
import { Vorgangsabschnitt } from "../Vorgangsabschnitt.js";
import { useSectionFolds } from "../vorgangsabschnitt/sectionFolds.js";
import { FilesReadSection, filesContent, useLoadedState } from "../vorgangsabschnitt/sections.js";
import { SECTION_TEXT } from "../vorgangsabschnitt/texts.js";
import type { DocumentFilesClient } from "../Vorgangsdateien.js";
import { KiKnopf } from "../meHuellen.js";
import { betragText } from "../vorgangsdetail.js";
import { ReplacesChips } from "../offerSuccession/ReplacesChips.js";
import { seriesAnchorId } from "../offerSuccession/seriesJump.js";
import { ROW_TEXT } from "../../views/customerOverview/texts.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { formatDatum } from "../../format.js";
import { ANGEBOT_STATUS_LABEL, AUFTRAG_STATUS_LABEL } from "../../vorgangsstatus.js";
import { HistoricalChip } from "./HistoricalChip.js";
import { HistoricalRecordPanel, useHistoricalOffers } from "./HistoricalRecordPanel.js";
import type { HistoricalKindClient, HistoricalRecordClient } from "./HistoricalRecordPanel.js";
import { HISTORICAL_TEXT as T } from "./texts.js";
import type { HistoricalKind } from "./texts.js";
import "./historical.css";

/** Was das historische Detail braucht: den Abschnitt „Dateien“ und das Panel seiner Art. */
export type HistoricalDetailClient<A extends HistoricalKind> = DocumentFilesClient & HistoricalRecordClient<A>;

export type HistoricalDetailProps = HistoricalKindClient<DocumentFilesClient> & {
  kundeId: string;
  record: Angebot | Auftrag;
  companies: readonly Unternehmen[];
  /** Ansprechpartner und Benutzer, wie sie der Rahmen ohnehin lädt. */
  umfeld: { kontakte: readonly Ansprechpartner[]; benutzer: readonly Benutzer[] };
  bearbeitung: boolean;
  gesperrt: boolean;
  /** Der Vorgang steht allein (Bereich): Notiz und Dateien beginnen offen. In der Liste der Akte (`false`) beginnen sie
   *  zu, und die Dateien laden erst beim Aufklappen — sonst riefe jede Zeile `listDocumentFiles` ab. */
  standalone: boolean;
  onBearbeiten: () => void;
  onSchliessen: () => void;
  onNeuLaden: () => void | Promise<unknown>;
  onOrdnerOeffnen: (folderId: string) => void;
  /** Am Angebot: ein Nachfolge-Chip springt zur Reihe (`jumpToSeries`, wie im vollen Detail). */
  onOpenSeries?: (offerId: string) => void;
};

/** Art und Klient als ein Wert — das Panel braucht genau die Wege SEINER Art (`HistoricalKindClient`). */
const kindClient = (p: HistoricalKindClient<DocumentFilesClient>): HistoricalKindClient =>
  (p.art === "offer" ? { art: "offer", client: p.client } : { art: "order", client: p.client });

/**
 * Das DETAIL eines historischen Angebots bzw. Auftrags (Spec „Historische Vorgänge anlegen und anzeigen“): Kopfzeile mit
 * dem Kennzeichen „historisch“ (am Angebot dazu die Nachfolge-Chips und der Sprunganker der Reihe), die Kopfdaten lesend,
 * am Angebot die Notiz; dazu der Abschnitt „Dateien“ — im aufgeschlagenen Vorgang offen, in der Liste der Akte zu
 * (`standalone`). Die EINZIGE Aktion ist „Bearbeiten“ — sie öffnet das Panel mit dem Vorgang; Senden, Version, Kopieren,
 * PDF und Vorschau gibt es für Altbestand nicht (der Server weist sie mit 409 HISTORISCHER_VORGANG ab). Die Kopfzeile
 * trägt die Klassen der jeweiligen Maske (`kang__`/`kauf__`), damit sie in Liste und Bereich aussieht wie ihre Nachbarn;
 * ein ersetztes historisches Angebot tritt über dieselbe Karte zurück wie ein echtes (`kang__card--replaced`).
 */
export function HistoricalDetail(p: HistoricalDetailProps): ReactElement {
  const { client, kundeId, art, record, companies } = p;
  const block = art === "offer" ? "kang" : "kauf";
  const offer = art === "offer" ? (record as Angebot) : null;
  const order = art === "order" ? (record as Auftrag) : null;
  const sections = useSectionFolds({ head: true, note: p.standalone, files: p.standalone });
  const loaded = useLoadedState();
  const historicalOffers = useHistoricalOffers(p.art === "order" ? p.client : null, kundeId, p.bearbeitung);
  const shortCode = companies.find((c) => c.id === record.companyId)?.shortCode ?? null;
  const status = offer !== null ? ANGEBOT_STATUS_LABEL[offer.status] : AUFTRAG_STATUS_LABEL[order!.status];
  const date = offer?.createdOn ?? order?.orderedOn ?? "";
  const files = {
    client, kundeId, bezug: { art: art === "offer" ? "offers" as const : "orders" as const, id: record.id },
    firmen: companies, vorgangName: ROW_TEXT.firstLink(art === "offer" ? "OFFER" : "SALES_ORDER", record.displayNumber),
    lesend: null, onOrdnerOeffnen: p.onOrdnerOeffnen,
  };
  const idOf = (field: string): string => `${block}-hist-${record.id}-${field}`;
  const readOnly = (field: string, label: string, value: string): ReactElement => (
    <Feld id={idOf(field)} label={label} wert={value} bearbeitbar={false} onChange={() => {}} />
  );

  const content = (
    <>
      <div className={`${block}__zeile`} {...(offer === null ? {} : { id: seriesAnchorId(offer.seriesId), tabIndex: -1 })}>
        <span className={`${block}__nummer`}>{record.displayNumber}</span>
        <HistoricalChip />
        {shortCode !== null && <Chip {...hausfarbeChip(companies, shortCode)}>{shortCode}</Chip>}
        <span className={`${block}__eintrag-titel`}>{record.title}</span>
        <span className={`${block}__status ${block}__status--${record.status.toLowerCase()}`}>{status}</span>
        {offer !== null && (
          <ReplacesChips replaces={offer.replaces} replacedBy={offer.replacedBy} disabled={p.gesperrt || p.bearbeitung}
            onOeffnen={(offerId) => p.onOpenSeries?.(offerId)} />
        )}
      </div>

      {!p.bearbeitung && (
        <>
          <Vorgangsabschnitt art={art === "offer" ? "angebot" : "auftrag"} titel={SECTION_TEXT.head}
            faltung={sections.head} inhaltId={idOf("kopfdaten")}>
            <div className="hist__fields">
              {readOnly("legacy", T.legacyNumber, record.legacyNumber ?? "")}
              {readOnly("date", T.date, date === "" ? "" : formatDatum(date))}
              {/* Ein Betrag steht rechtsbündig mit der Einheit daneben — gesperrt wie im Panel (Hausregel „Zahlen
                  rechtsbündig“), nicht als Fließtext. */}
              <Eingabe id={idOf("amount")} name={idOf("amount")} beschriftung={T.netAmount} art="dezimal"
                einheit={T.currencyUnit} wert={betragText(record.netAmount)} gesperrt />
              {readOnly("status", T.status, status)}
              {readOnly("contact", T.contact, record.contactName ?? "")}
              {readOnly("responsible", T.responsible, record.responsibleUserName ?? "")}
              {order !== null && readOnly("source", T.sourceOffer, order.sourceOfferNumber ?? "")}
            </div>
          </Vorgangsabschnitt>
          {offer !== null && (
            <Vorgangsabschnitt art="notiz" titel={SECTION_TEXT.note} faltung={sections.note} inhaltId={idOf("notiz")}>
              <p className="hist__note">{(offer.note ?? "").trim() === "" ? SECTION_TEXT.empty : offer.note}</p>
            </Vorgangsabschnitt>
          )}
          <FilesReadSection faltung={sections.files} inhaltId={idOf("dateien")}
            content={filesContent(files, sections.files.offen, loaded)} />
          <div className={`${block}__aktionen`}>
            <KiKnopf name={T.editName(record.title)} deaktiviert={p.gesperrt} onClick={p.onBearbeiten}>{T.edit}</KiKnopf>
          </div>
        </>
      )}
    </>
  );

  return (
    <>
      {/* Am Angebot die KARTE wie im vollen Detail (`display: contents`, AngebotDetail.css): An einer ersetzten Reihe
          tritt sie zurück bis auf die Nachfolge-Chips. Das Panel steht AUSSERHALB — Ebenen über der Karte dimmen nicht mit. */}
      {offer === null ? content
        : <div className={offer.replaced ? "kang__card kang__card--replaced" : "kang__card"}>{content}</div>}

      {p.bearbeitung && (
        <HistoricalRecordPanel {...kindClient(p)} kundeId={kundeId} vorhanden={record} firmen={companies}
          umfeld={{ ...p.umfeld, historischeAngebote: historicalOffers }}
          onGespeichert={() => { p.onSchliessen(); void p.onNeuLaden(); }}
          onAbbrechen={p.onSchliessen} />
      )}
    </>
  );
}
